import { useCallback, useEffect, useState, useRef } from 'react'
import useTokens from './useTokens'
import { useEthers } from './EthersProvider'
import useIsMounted from '~hooks/useIsMounted'

export default function useConvert(address, xdataAmountWei, onComplete) {
    const [converting, setConverting] = useState(false)

    const busyRef = useRef(false)

    const propsRef = useRef({
        address: undefined,
        metamaskProvider: undefined,
        xdataAmountWei: undefined,
    })

    const { metamaskProvider, providers } = useEthers()

    const { Web3Provider } = providers || {}

    const { xdataToken } = useTokens()

    const scheduleConvert = useCallback(() => {
        if (busyRef.current) {
            return
        }
        busyRef.current = true

        setConverting(true)

        propsRef.current = {
            address,
            xdataAmountWei,
        }
    }, [address, xdataAmountWei])

    const isMounted = useIsMounted()

    const onCompleteRef = useRef(onComplete)

    onCompleteRef.current = onComplete

    useEffect(() => {
        if (!xdataToken || !Web3Provider || !metamaskProvider) {
            return
        }

        if (!converting) {
            busyRef.current = false
            return
        }

        (async () => {
            try {
                const { current: props } = propsRef

                const xdata = await xdataToken.balanceOf(props.address)

                if (xdata.lt(props.xdataAmountWei)) {
                    throw new Error('Unsufficient funds.')
                }

                const signer = new Web3Provider(metamaskProvider).getSigner()

                const tx = await xdataToken.connect(signer).upgrade(props.xdataAmountWei)

                await tx.wait()

                if (typeof onCompleteRef.current === 'function') {
                    onCompleteRef.current()
                }
            } catch (e) {
                console.error('Conversion failure', e)

                if (typeof onCompleteRef.current === 'function') {
                    onCompleteRef.current(e)
                }
            } finally {
                if (isMounted()) {
                    setConverting(false)
                }
            }
        })()
    }, [converting, isMounted, xdataToken, Web3Provider, metamaskProvider])

    return {
        convert: scheduleConvert,
        converting,
    }
}
